import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { searchCards, ApiError } from '../lib/api';
import { withRetry, isRetryableError } from '../lib/retry';
import {
  getCachedSearchWithTimestamp,
  setCachedSearch,
  clearToken,
  getStructuredCacheKey,
} from '../lib/storage';
import { parseSearchQuery, type ParsedQuery } from '../lib/searchParser';
import { buildSetCodeMap } from '../lib/setCodeData';
import type { Card, SearchResponse } from '../lib/types';

export interface ResilientSearchResult {
  /** Current search query */
  query: string;
  /** Search results (cards) */
  cards: Card[];
  /** Whether a search is in progress */
  isLoading: boolean;
  /** Error message if search failed */
  error: string | null;
  /** Whether results are from cache */
  cacheHit: boolean;
  /** Parsed query with interpretation */
  parsedQuery: ParsedQuery | null;
  /** Handle query change (with debounce) */
  handleQueryChange: (query: string) => void;
  /** Clear search state */
  clearSearch: () => void;
  /** Whether showing stale (expired) cached data */
  isStale: boolean;
  /** Timestamp of cached data (if showing cached) */
  cacheTimestamp: number | null;
  /** Whether a background revalidation is in progress */
  isRevalidating: boolean;
  /** Manually retry the last search */
  retry: () => void;
}

/**
 * Enhanced search hook with stale-while-revalidate pattern.
 *
 * Flow:
 * 1. Parse query
 * 2. Check cache → if exists, show immediately (cacheHit=true)
 * 3. Start API fetch with retry
 * 4. On success: update cards, update cache, cacheHit=false
 * 5. On error with cache: keep showing cached, isStale=true
 * 6. On error without cache: show error state
 */
export function useResilientSearch(): ResilientSearchResult {
  const [query, setQuery] = useState('');
  const [cards, setCards] = useState<Card[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cacheHit, setCacheHit] = useState(false);
  const [parsedQuery, setParsedQuery] = useState<ParsedQuery | null>(null);
  const [isStale, setIsStale] = useState(false);
  const [cacheTimestamp, setCacheTimestamp] = useState<number | null>(null);
  const [isRevalidating, setIsRevalidating] = useState(false);

  // Build set code map once
  const setCodeMap = useMemo(() => buildSetCodeMap(), []);

  // Debounce timer ref
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Store last search params for retry
  const lastSearchRef = useRef<{ parsed: ParsedQuery; cacheKey: string } | null>(null);

  const search = useCallback(
    async (searchQuery: string) => {
      const trimmedQuery = searchQuery.trim();

      // Parse the query for structured search
      const parsed = parseSearchQuery(trimmedQuery, setCodeMap);
      setParsedQuery(trimmedQuery.length >= 2 ? parsed : null);

      // Clear results if query is too short
      if (trimmedQuery.length < 2 || !parsed.name) {
        setCards([]);
        setError(null);
        setCacheHit(false);
        setIsStale(false);
        setCacheTimestamp(null);
        setIsRevalidating(false);
        lastSearchRef.current = null;
        return;
      }

      // Build cache key for structured query
      const cacheKey = getStructuredCacheKey(parsed.name, parsed.number, parsed.setId);

      // Store for retry
      lastSearchRef.current = { parsed, cacheKey };

      setIsLoading(true);
      setError(null);
      setIsStale(false);

      try {
        // Step 1: Check cache first (including expired entries for stale-while-revalidate)
        const cached = await getCachedSearchWithTimestamp(cacheKey);

        if (cached) {
          // Show cached data immediately
          setCards(cached.response.cards || []);
          setCacheHit(true);
          setCacheTimestamp(cached.timestamp);
          setIsLoading(false);

          // If cache is fresh, we're done
          if (!cached.isExpired) {
            return;
          }

          // Cache is expired - start background revalidation
          setIsRevalidating(true);
        }

        // Step 2: Fetch from API with retry
        const response = await withRetry<SearchResponse>(
          () =>
            searchCards({
              name: parsed.name,
              number: parsed.number,
              setId: parsed.setId,
            }),
          { maxRetries: 3 }
        );

        if (response.success) {
          setCards(response.cards || []);
          // Cache the fresh response
          await setCachedSearch(cacheKey, response);
          setCacheHit(false);
          setCacheTimestamp(null);
          setIsStale(false);
        } else {
          // API returned error response
          if (cached) {
            // We have stale cache, keep showing it
            setIsStale(true);
            setError(response.error || 'Failed to refresh');
          } else {
            setError(response.error || 'Search failed');
            setCards([]);
          }
        }
      } catch (err) {
        console.error('Search error:', err);

        // Handle auth errors by clearing token and reloading
        if (err instanceof ApiError && err.status === 401) {
          await clearToken();
          window.location.reload();
          return;
        }

        // Check if we have cached data to fall back to
        const cached = await getCachedSearchWithTimestamp(cacheKey);
        if (cached) {
          // Keep showing cached data, mark as stale
          setCards(cached.response.cards || []);
          setCacheHit(true);
          setCacheTimestamp(cached.timestamp);
          setIsStale(true);
          // Only show error if it's not a retryable network error (user knows they're offline)
          if (!isRetryableError(err)) {
            setError(err instanceof Error ? err.message : 'Search failed');
          }
        } else {
          // No cache, show error
          setError(err instanceof Error ? err.message : 'Search failed');
          setCards([]);
        }
      } finally {
        setIsLoading(false);
        setIsRevalidating(false);
      }
    },
    [setCodeMap]
  );

  // Retry the last search
  const retry = useCallback(() => {
    if (query) {
      search(query);
    }
  }, [query, search]);

  // Debounced search
  const debouncedSearch = useCallback(
    (searchQuery: string) => {
      // Clear previous debounce
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      // Set new debounce
      debounceRef.current = setTimeout(() => {
        search(searchQuery);
      }, 300);
    },
    [search]
  );

  // Handle query change with debounce
  const handleQueryChange = useCallback(
    (newQuery: string) => {
      setQuery(newQuery);
      debouncedSearch(newQuery);
    },
    [debouncedSearch]
  );

  // Clear search
  const clearSearch = useCallback(() => {
    setQuery('');
    setCards([]);
    setError(null);
    setCacheHit(false);
    setIsStale(false);
    setCacheTimestamp(null);
    setIsRevalidating(false);
    lastSearchRef.current = null;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    query,
    cards,
    isLoading,
    error,
    cacheHit,
    parsedQuery,
    handleQueryChange,
    clearSearch,
    isStale,
    cacheTimestamp,
    isRevalidating,
    retry,
  };
}
